/**
 * 图片检查结果面板 - 显示无效图片并支持一键清除
 */
import React from 'react';
import { OutlineItem } from '../types';
import {
  CheckCircleIcon,
  XMarkIcon,
  ArrowPathIcon,
  PhotoIcon,
} from '@heroicons/react/24/outline';

interface ImageInfo {
  line: number;
  alt: string;
  url: string;
  original: string;
  status: 'checking' | 'exists' | 'broken';
  errorMessage?: string;
}

interface ChapterImageResult {
  item: OutlineItem;
  images: ImageInfo[];
}

interface ContentEditImageCheckPanelProps {
  show: boolean;
  results: ChapterImageResult[] | null;
  clearingIds: Set<string>;
  clearingAll: boolean;
  onClose: () => void;
  onClearChapter: (chapter: ChapterImageResult) => void;
  onClearAll: () => void;
}

const ContentEditImageCheckPanel: React.FC<ContentEditImageCheckPanelProps> = ({
  show,
  results,
  clearingIds,
  clearingAll,
  onClose,
  onClearChapter,
  onClearAll,
}) => {
  if (!show || results === null) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="fixed inset-0 bg-black/30" onClick={onClose} />
      <div className="relative bg-white rounded-lg shadow-2xl max-w-2xl w-full mx-4 max-h-[80vh] flex flex-col z-10">
        {/* 标题栏 */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center">
            <PhotoIcon className="w-5 h-5 mr-2 text-gray-500" />
            图片检查结果
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        {/* 内容区 */}
        <div className="overflow-y-auto p-6 space-y-4">
          {results.length === 0 ? (
            <div className="flex items-center text-green-600 py-4">
              <CheckCircleIcon className="w-5 h-5 mr-2" />
              <span>所有插入的图片均有效，无需清理</span>
            </div>
          ) : (
            <>
              <p className="text-sm text-gray-500">
                发现 {results.reduce((sum, ch) => sum + ch.images.length, 0)} 个无效图片，分布在 {results.length} 个章节中
              </p>
              {results.map((chapter, idx) => (
                <div key={chapter.item.id} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex-1 min-w-0">
                      <span className="text-xs text-gray-400 mr-2">#{idx + 1}</span>
                      <span className="font-medium text-gray-900 text-sm">
                        {chapter.item.id} {chapter.item.title}
                      </span>
                      <span className="ml-2 text-xs text-red-500">
                        ({chapter.images.length} 个无效图片)
                      </span>
                    </div>
                    <button
                      onClick={() => onClearChapter(chapter)}
                      disabled={clearingIds.has(chapter.item.id)}
                      className="inline-flex items-center px-3 py-1 text-xs font-medium text-white bg-red-500 hover:bg-red-600 disabled:opacity-50 disabled:cursor-not-allowed rounded transition-colors flex-shrink-0 ml-3"
                    >
                      {clearingIds.has(chapter.item.id) ? (
                        <>
                          <svg className="animate-spin w-3 h-3 mr-1" viewBox="0 0 24 24" fill="none">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                          </svg>
                          清除中...
                        </>
                      ) : (
                        <>
                          <XMarkIcon className="w-3 h-3 mr-1" />
                          清除
                        </>
                      )}
                    </button>
                  </div>

                  {/* 图片列表 */}
                  <div className="space-y-2">
                    {chapter.images.map((img, imgIdx) => (
                      <div key={imgIdx} className="text-xs bg-red-50 rounded px-3 py-2">
                        <div className="flex items-start justify-between">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="text-red-400 font-mono flex-shrink-0">行 {img.line}</span>
                              {img.alt && (
                                <span className="text-gray-700 font-medium truncate">
                                  描述: {img.alt}
                                </span>
                              )}
                            </div>
                            <code className="block text-gray-500 break-all bg-red-100/50 rounded px-2 py-1 mt-1 font-mono">
                              {img.original}
                            </code>
                            {img.errorMessage && (
                              <p className="text-red-500 mt-1">{img.errorMessage}</p>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </>
          )}
        </div>

        {/* 底部操作栏 */}
        <div className="px-6 py-3 border-t border-gray-200 flex justify-between">
          <div>
            {results.length > 0 && (
              <button
                onClick={onClearAll}
                disabled={clearingAll}
                className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-red-500 hover:bg-red-600 disabled:opacity-50 disabled:cursor-not-allowed rounded transition-colors"
              >
                {clearingAll ? (
                  <>
                    <svg className="animate-spin w-4 h-4 mr-1.5" viewBox="0 0 24 24" fill="none">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                    正在清除...
                  </>
                ) : (
                  <>
                    <ArrowPathIcon className="w-4 h-4 mr-1.5" />
                    全部清除
                  </>
                )}
              </button>
            )}
          </div>
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm text-gray-700 bg-gray-100 hover:bg-gray-200 rounded transition-colors"
          >
            关闭
          </button>
        </div>
      </div>
    </div>
  );
};

export default ContentEditImageCheckPanel;
